import React, { useCallback, useEffect, useMemo, useRef, useState, createElement } from 'react';
import { supabase } from '../../lib/supabase';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { MetricsDateFilter } from './MetricsDateFilter';
import { Banknote, CreditCard, Smartphone, Download, RefreshCw, Receipt, Utensils, Bike, ShoppingBag, Ban, AlertCircle } from 'lucide-react';
// ──────────────────────────────────────────────────────────────────
// CashClose — La Cabaña Parrillera + NatuCafé + Emilius
// Renders a per-product sales log for a chosen date range, with
// timestamp, price, payment method and channel (mesa / delivery /
// pickup). Reads from the `payments` table (items_json snapshot)
// joined with `orders` for delivery_type. Read-only — does NOT mutate
// any data.
//
// El componente tiene su PROPIO filtro de rango interno (mismos
// presets que MetricsDateFilter: Hoy / Ayer / Esta Semana / Este Mes
// / Último Mes / 3 Meses + Desde/Hasta). Es INDEPENDIENTE del filtro
// global de MetricsPage — cumplen funciones distintas.
// ──────────────────────────────────────────────────────────────────
interface CabanaCashCloseProps {
  restaurantId: number | string;
}
interface PaymentRow {
  id: string;
  restaurant_id: number;
  order_id: string;
  table_id: string | null;
  amount: number;
  method: string;
  items_json: any[];
  created_at: string;
  is_partial: boolean;
}
interface OrderMeta {
  id: string;
  delivery_type: string | null;
  table_id: number | null;
  order_number: string | null;
  // Express / domicilio fee charged to this order. Lives on the order
  // itself (not on the payment row's items_json), so we must surface it
  // separately to avoid undercounting the day's revenue.
  delivery_fee: number;
  // Authoritative order total from `orders.total`. Used as the ceiling
  // when flattening payments into per-item rows so the cash close
  // always matches Dashboard / `v_sales_summary_today` exactly,
  // regardless of what was written to payments.items_json.
  order_total: number;
}
interface FlatRow {
  payment_id: string;
  order_id: string;
  order_number: string | null;
  created_at: string;
  method: string;
  channel: 'mesa' | 'delivery' | 'pickup' | 'desconocido';
  table_id: number | null;
  /** Human-readable table label (e.g. "Mesa 7" or "Barra 2"). Null if not a mesa order. */
  table_label: string | null;
  item_name: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  notes: string | null;
}
interface VoidLog {
  id: number;
  restaurant_id: number;
  order_id: number | null;
  item_id: number | null;
  action_type: string;
  authorized_name: string;
  reason: string;
  amount: number;
  detail: string | null;
  created_at: string;
}
// ─── Carga completa (paginada) ────────────────────────────────────
// La API devuelve un máximo de filas por respuesta. Pedimos páginas
// consecutivas con orden estable y avanzamos por la cantidad REAL
// recibida hasta que una página llega vacía, así funciona aunque el
// servidor tenga un tope menor que PAGE_SIZE. Cualquier error aborta la
// carga completa: nunca se muestra una suma parcial.
const PAGE_SIZE = 1000;
const ID_CHUNK = 200;
async function fetchAllPages<T extends {
  id: unknown;
}>(label: string, buildPage: (from: number, to: number) => PromiseLike<{
  data: T[] | null;
  error: {
    message: string;
  } | null;
}>): Promise<T[]> {
  const out: T[] = [];
  const seen = new Set<string>();
  let offset = 0;
  for (let guard = 0; guard < 100000; guard++) {
    const {
      data,
      error
    } = await buildPage(offset, offset + PAGE_SIZE - 1);
    if (error) {
      throw new Error(`No se pudieron cargar todos los datos (${label}): ${error.message}`);
    }
    const rows = data || [];
    if (rows.length === 0) return out;
    for (const r of rows) {
      const k = String(r.id);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(r);
    }
    offset += rows.length;
  }
  throw new Error(`Paginación sin fin detectada (${label}).`);
}
function chunkIds(ids: string[]): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += ID_CHUNK) {
    out.push(ids.slice(i, i + ID_CHUNK));
  }
  return out;
}
// Día calendario siguiente (YYYY-MM-DD) para el límite EXCLUSIVO.
const nextDayIso = (ymd: string) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().substring(0, 10);
};
// Estados de pedido marcados como anulados. Se usan SOLO para informar
// qué parte de los cobros pertenece a pedidos hoy anulados — no se
// descuentan del total, porque el estado actual no prueba un reembolso.
const CANCELLED_STATUSES = new Set(['cancelled', 'canceled', 'cancelado', 'refunded', 'reembolsado', 'voided', 'rejected']);
const isCancelledStatus = (s: unknown) => CANCELLED_STATUSES.has(String(s || '').toLowerCase().trim());
interface UnpaidSale {
  order_id: string;
  order_number: string | null;
  created_at: string;
  total: number;
  method: string;
}
const todayIso = () => {
  // Fecha calendario de COSTA RICA (UTC-6), NO la del navegador. Si usáramos
  // d.getFullYear()/getDate() tomaríamos el día local del dispositivo: cuando
  // la PC está en otra zona, cerca de medianoche el día local ≠ día CR y la
  // ventana del cierre (que ya usa -06:00) se armaría sobre el día equivocado.
  // `en-CA` formatea directamente como YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Costa_Rica',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
};
const methodMeta = (m: string) => {
  switch (m) {
    case 'cash':
      return {
        label: 'Efectivo',
        Icon: Banknote,
        chip: 'bg-emerald-50 text-emerald-700 border-emerald-200'
      };
    case 'sinpe':
      return {
        label: 'SINPE',
        Icon: Smartphone,
        chip: 'bg-blue-50 text-blue-700 border-blue-200'
      };
    case 'card':
    case 'tarjeta':
      return {
        label: 'Tarjeta',
        Icon: CreditCard,
        chip: 'bg-violet-50 text-violet-700 border-violet-200'
      };
    case 'transfer':
    case 'transferencia':
      return {
        label: 'Transferencia',
        Icon: Smartphone,
        chip: 'bg-blue-50 text-blue-700 border-blue-200'
      };
    case 'unknown':
    case '':
    case 'sin_metodo':
      return {
        label: 'Sin método',
        Icon: Receipt,
        chip: 'bg-gray-100 text-gray-700 border-gray-300'
      };
    default:
      return {
        label: m,
        Icon: Banknote,
        chip: 'bg-gray-50 text-gray-700 border-gray-200'
      };
  }
};
const channelMeta = (c: FlatRow['channel'], tableLabel: string | null) => {
  switch (c) {
    case 'mesa':
      return {
        label: tableLabel || 'Mesa',
        Icon: Utensils,
        chip: 'bg-amber-50 text-amber-700 border-amber-200'
      };
    case 'delivery':
      return {
        label: 'Delivery',
        Icon: Bike,
        chip: 'bg-cyan-50 text-cyan-700 border-cyan-200'
      };
    case 'pickup':
      return {
        label: 'Para llevar',
        Icon: ShoppingBag,
        chip: 'bg-orange-50 text-orange-700 border-orange-200'
      };
    default:
      return {
        label: '—',
        Icon: Receipt,
        chip: 'bg-gray-50 text-gray-600 border-gray-200'
      };
  }
};
const fmtTime = (iso: string) => {
  try {
    return new Date(iso).toLocaleTimeString('es-CR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch {
    return iso;
  }
};
const fmtFull = (iso: string) => {
  try {
    return new Date(iso).toLocaleString('es-CR', {
      dateStyle: 'short',
      timeStyle: 'medium'
    });
  } catch {
    return iso;
  }
};
// Fecha y hora SIEMPRE en horario Costa Rica (America/Costa_Rica), para que
// al revisar rangos de varios días cada anulación muestre el día correcto
// sin depender de la zona horaria del navegador del cajero.
const fmtDateCR = (iso: string) => {
  try {
    return new Date(iso).toLocaleDateString('es-CR', {
      timeZone: 'America/Costa_Rica',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return iso;
  }
};
const fmtTimeCR = (iso: string) => {
  try {
    return new Date(iso).toLocaleTimeString('es-CR', {
      timeZone: 'America/Costa_Rica',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch {
    return iso;
  }
};
// ════════════════════════════════════════════════════════════════════
// getCashCloseTotals — ÚNICA FUENTE DE VERDAD del Cierre de Caja.
// Calcula TODO exclusivamente desde payments.amount. NO usa items_json,
// orders.total, flatRows, ni el status de la orden. Es una función pura:
// recibe los payments ya cargados y devuelve el total del día + el
// desglose por método. Las tarjetas del visual leen ÚNICAMENTE de aquí.
// ════════════════════════════════════════════════════════════════════
export interface CashCloseTotals {
  totalDelDia: number;
  byMethod: Record<string, {
    total: number;
    count: number;
  }>;
  paymentsCount: number;
  paymentIds: Array<[string, number, string, string]>;
}
export function getCashCloseTotals(payments: PaymentRow[]): CashCloseTotals {
  const byMethod: Record<string, {
    total: number;
    count: number;
  }> = {
    cash: {
      total: 0,
      count: 0
    },
    card: {
      total: 0,
      count: 0
    },
    sinpe: {
      total: 0,
      count: 0
    }
  };
  let totalDelDia = 0;
  const paymentIds: Array<[string, number, string, string]> = [];
  for (const p of payments) {
    const amt = Number(p.amount) || 0;
    totalDelDia += amt;
    const m = (p.method || 'sin_metodo') as string;
    if (!byMethod[m]) byMethod[m] = {
      total: 0,
      count: 0
    };
    byMethod[m].total += amt;
    byMethod[m].count += 1;
    paymentIds.push([String(p.id), amt, m, String(p.created_at)]);
  }
  return {
    totalDelDia,
    byMethod,
    paymentsCount: payments.length,
    paymentIds
  };
}
export function CabanaCashClose({
  restaurantId
}: CabanaCashCloseProps) {
  // ── Filtro de rango interno. Default: hoy. Controlado por el mismo
  // <MetricsDateFilter> que usa el filtro global de MetricsPage para
  // mantener UI consistente, pero su estado vive AQUÍ — totalmente
  // independiente del filtro de arriba.
  const [startDate, setStartDate] = useState<string>(todayIso());
  const [endDate, setEndDate] = useState<string>(todayIso());
  // Rango "es hoy" → start=end=hoy. Usado para auto-refresh y copy.
  const rangeIsToday = startDate === todayIso() && endDate === todayIso();
  const isSingleDay = startDate === endDate;
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [orderMeta, setOrderMeta] = useState<Record<string, OrderMeta>>({});
  const [voidLogs, setVoidLogs] = useState<VoidLog[]>([]);
  /** Maps restaurant_tables.id → human label (e.g. "Mesa 7" / "Barra 2"). */
  const [tableLabels, setTableLabels] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Órdenes cerradas sin NINGÚN pago registrado en todo el historial. */
  const [unpaidSales, setUnpaidSales] = useState<UnpaidSale[]>([]);
  /** Order ids cuyo estado actual es anulado/cancelado/reembolsado. */
  const [cancelledOrderIds, setCancelledOrderIds] = useState<Set<string>>(new Set());
  const [voidError, setVoidError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const load = useCallback(async () => {
    if (!restaurantId) return;
    const requestId = ++requestIdRef.current;
    const rid = Number(restaurantId);
    setLoading(true);
    setError(null);
    try {
      // Ventana Costa Rica (UTC-6 fijo, sin horario de verano):
      // inicio inclusivo 00:00 del día "desde", fin EXCLUSIVO 00:00 del
      // día siguiente a "hasta".
      const startIso = new Date(`${startDate}T00:00:00-06:00`).toISOString();
      const endIsoExclusive = new Date(`${nextDayIso(endDate)}T00:00:00-06:00`).toISOString();

      // ─── (0) Anulaciones (auditoría) ───────────────────────────────
      // Se cargan aparte: un fallo acá no invalida los cobros, pero sí
      // se avisa en su sección en vez de mostrar una lista incompleta.
      let voidsLoaded: VoidLog[] = [];
      let voidsErr: string | null = null;
      try {
        voidsLoaded = await fetchAllPages<VoidLog>('anulaciones', (f, t) => supabase.from('void_audit_log').select('*').eq('restaurant_id', rid).gte('created_at', startIso).lt('created_at', endIsoExclusive).order('created_at', {
          ascending: false
        }).order('id', {
          ascending: false
        }).range(f, t));
      } catch (e: any) {
        console.error('[CabanaCashClose] error loading void logs:', e);
        voidsErr = e?.message || 'No se pudieron cargar las anulaciones.';
      }

      // ─── (1) Cobros registrados: payments por payments.created_at ──
      // NO se exige que el pedido haya sido creado dentro del rango.
      const pays = await fetchAllPages<PaymentRow>('cobros', (f, t) => supabase.from('payments').select('id, restaurant_id, order_id, table_id, amount, method, items_json, created_at, is_partial').eq('restaurant_id', rid).gte('created_at', startIso).lt('created_at', endIsoExclusive).order('created_at', {
        ascending: true
      }).order('id', {
        ascending: true
      }).range(f, t));
      const paymentOrderIds = Array.from(new Set(pays.map(p => p.order_id != null ? String(p.order_id) : '').filter(Boolean)));

      // ─── (2) Metadata de las órdenes de esos cobros (canal / mesa) ─
      const ORDER_COLS = 'id, order_number, delivery_type, table_id, payment_method, total, status, created_at';
      const paymentOrders: any[] = [];
      for (const chunk of chunkIds(paymentOrderIds)) {
        const rows = await fetchAllPages<any>('pedidos de los cobros', (f, t) => supabase.from('orders').select(ORDER_COLS).eq('restaurant_id', rid).in('id', chunk).order('id', {
          ascending: true
        }).range(f, t));
        paymentOrders.push(...rows);
      }

      // ─── (3) Respaldo histórico: ventas SIN pago registrado ────────
      // Órdenes creadas en el rango y cerradas (mismo criterio que antes),
      // no anuladas, con total > 0. Antes de clasificarlas como "sin
      // pago", se verifica que no tengan NINGÚN pago en todo el historial
      // (un pago fuera del rango igual cuenta como pagada).
      const closedOrders = await fetchAllPages<any>('pedidos cerrados', (f, t) => supabase.from('orders').select(ORDER_COLS).eq('restaurant_id', rid).gte('created_at', startIso).lt('created_at', endIsoExclusive).or('status.eq.delivered,status.eq.completed,status.eq.paid,payment_method.not.is.null').order('created_at', {
        ascending: true
      }).order('id', {
        ascending: true
      }).range(f, t));
      const paidInWindow = new Set(paymentOrderIds);
      const candidates = closedOrders.filter((o: any) => !isCancelledStatus(o.status) && (Number(o.total) || 0) > 0 && !paidInWindow.has(String(o.id)));
      const paidAnytime = new Set<string>();
      for (const chunk of chunkIds(candidates.map((o: any) => String(o.id)))) {
        const rows = await fetchAllPages<any>('historial de pagos', (f, t) => supabase.from('payments').select('id, order_id').eq('restaurant_id', rid).in('order_id', chunk).order('id', {
          ascending: true
        }).range(f, t));
        rows.forEach((r: any) => paidAnytime.add(String(r.order_id)));
      }
      const unpaid: UnpaidSale[] = candidates.filter((o: any) => !paidAnytime.has(String(o.id))).map((o: any) => ({
        order_id: String(o.id),
        order_number: o.order_number || null,
        created_at: String(o.created_at),
        total: Number(o.total) || 0,
        method: String(o.payment_method || '').toLowerCase().trim() || 'sin_metodo'
      }));

      // Una respuesta más nueva ya está en camino: descartar ésta.
      if (requestId !== requestIdRef.current) return;
      const allOrders = [...paymentOrders, ...closedOrders];
      const metaMap: Record<string, OrderMeta> = {};
      allOrders.forEach((o: any) => {
        metaMap[String(o.id)] = {
          id: String(o.id),
          delivery_type: o.delivery_type,
          table_id: o.table_id != null ? Number(o.table_id) : null,
          order_number: o.order_number || null,
          delivery_fee: Math.max(0, Number(o.delivery_fee) || 0),
          order_total: Math.max(0, Number(o.total) || 0)
        };
      });
      setOrderMeta(metaMap);
      setCancelledOrderIds(new Set(paymentOrders.filter((o: any) => isCancelledStatus(o.status)).map((o: any) => String(o.id))));
      setPayments(pays);
      setUnpaidSales(unpaid);
      setVoidLogs(voidsLoaded);
      setVoidError(voidsErr);

      // ─── (4) Etiquetas de mesa (solo presentación) ─────────────────
      const tableIds = Array.from(new Set(allOrders.map((o: any) => o.table_id).filter((v: any) => v != null).map((v: any) => Number(v)).concat(pays.map(p => p.table_id != null ? Number(p.table_id) : null).filter((v): v is number => v != null))));
      if (tableIds.length > 0) {
        const {
          data: tablesData,
          error: tblErr
        } = await supabase.from('tables').select('id, table_number, table_name').in('id', tableIds);
        if (requestId !== requestIdRef.current) return;
        if (tblErr) {
          console.warn('[CabanaCashClose] could not resolve table labels', tblErr);
          setTableLabels({});
        } else {
          const tMap: Record<number, string> = {};
          (tablesData || []).forEach((t: any) => {
            const label = t.table_name && String(t.table_name).trim() || (t.table_number != null ? `Mesa ${t.table_number}` : `Mesa ${t.id}`);
            tMap[Number(t.id)] = label;
          });
          setTableLabels(tMap);
        }
      } else {
        setTableLabels({});
      }
    } catch (err: any) {
      if (requestId !== requestIdRef.current) return;
      console.error('[CabanaCashClose] load error:', err);
      // Sin totales parciales: se vacía todo y se muestra el error.
      setPayments([]);
      setUnpaidSales([]);
      setOrderMeta({});
      setCancelledOrderIds(new Set());
      setError(err?.message || 'Error cargando ventas del día.');
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [restaurantId, startDate, endDate]);
  useEffect(() => {
    load();
  }, [load]);
  // ─── Auto-refresh: only when the range is "today only" ───
  // Refreshes every 20s so newly processed payments appear without
  // forcing the user to click ⟳. Past dates / multi-day ranges never
  // auto-refresh.
  useEffect(() => {
    if (!rangeIsToday) return;
    const id = setInterval(() => {
      if (!loading) load();
    }, 20000);
    return () => clearInterval(id);
  }, [rangeIsToday, loading, load]);
  // Flatten payments → one row per item sold.
  //
  // KEY INVARIANT: for every revenue-valid order, the sum of its rows
  // equals `orders.total`. This is what makes the cash close match
  // Dashboard / `v_sales_summary_today` exactly. If items_json doesn't
  // already sum to the order total (e.g. delivery fee, tip, or any other
  // charge not embedded in items_json), we add a single reconciliation
  // row at the end so the math always closes.
  const flatRows: FlatRow[] = useMemo(() => {
    const rows: FlatRow[] = [];
    // Detalle por ítem para la tabla. SIN reconciliación y SIN relleno de
    // "Otros cargos": el TOTAL DEL DÍA se calcula aparte desde
    // payments.amount (ver useMemo `totals`). Esta tabla es sólo el log
    // de productos vendidos. Si un pago no trae desglose de ítems, se
    // muestra una línea única con su monto cobrado (payment.amount), de
    // modo que cada cobro siempre aparece en el log aunque no tenga items.
    for (const p of payments) {
      const meta = orderMeta[p.order_id];
      let channel: FlatRow['channel'] = 'desconocido';
      if (meta?.delivery_type === 'delivery') channel = 'delivery';else if (meta?.delivery_type === 'pickup') channel = 'pickup';else if (meta?.delivery_type === 'dine_in' || meta?.table_id != null) channel = 'mesa';else if (p.table_id != null) channel = 'mesa';
      // Resolve the human-readable mesa label using either the order's
      // table_id or the payment's table_id (whichever exists).
      const rawTableId = meta?.table_id ?? (p.table_id != null ? Number(p.table_id) : null);
      const resolvedLabel = rawTableId != null ? tableLabels[Number(rawTableId)] || null : null;
      const items = Array.isArray(p.items_json) ? p.items_json : [];
      if (items.length === 0) {
        // No item breakdown — show the payment's amount as a single line.
        const amt = Number(p.amount) || 0;
        rows.push({
          payment_id: p.id,
          order_id: p.order_id,
          order_number: meta?.order_number || null,
          created_at: p.created_at,
          method: p.method,
          channel,
          table_id: rawTableId,
          table_label: resolvedLabel,
          item_name: '(pago sin desglose)',
          quantity: 1,
          unit_price: amt,
          line_total: amt,
          notes: null
        });
      } else {
        for (const it of items) {
          const lineTotal = Number(it.line_total) || 0;
          rows.push({
            payment_id: p.id,
            order_id: p.order_id,
            order_number: meta?.order_number || null,
            created_at: p.created_at,
            method: p.method,
            channel,
            table_id: rawTableId,
            table_label: resolvedLabel,
            item_name: it.item_name || '—',
            quantity: Number(it.quantity) || 1,
            unit_price: Number(it.unit_price) || 0,
            line_total: lineTotal,
            notes: it.notes || null
          });
        }
      }
    }
    return rows;
  }, [payments, orderMeta, tableLabels]);
  // Totals
  const totals = useMemo(() => {
    // byMethod + total del día salen de getCashCloseTotals → la ÚNICA
    // fuente de verdad (SUM(payments.amount)). No se calculan aquí.
    const cc = getCashCloseTotals(payments);
    const byChannel: Record<string, {
      total: number;
      count: number;
    }> = {
      mesa: {
        total: 0,
        count: 0
      },
      delivery: {
        total: 0,
        count: 0
      },
      pickup: {
        total: 0,
        count: 0
      },
      desconocido: {
        total: 0,
        count: 0
      }
    };
    // El TOTAL DEL DÍA y el desglose por método vienen EXCLUSIVAMENTE de
    // getCashCloseTotals(payments) = SUM(payments.amount). El byChannel
    // necesita la metadata de la orden (mesa/delivery/pickup), así que se
    // acumula aquí sobre el MISMO payments.amount — no afecta ni el total
    // ni las tarjetas Efectivo/Tarjeta/SINPE.
    const grand = cc.totalDelDia;
    for (const p of payments) {
      const amt = Number(p.amount) || 0;
      const meta = orderMeta[p.order_id];
      let channel: FlatRow['channel'] = 'desconocido';
      if (meta?.delivery_type === 'delivery') channel = 'delivery';else if (meta?.delivery_type === 'pickup') channel = 'pickup';else if (meta?.delivery_type === 'dine_in' || meta?.table_id != null) channel = 'mesa';else if (p.table_id != null) channel = 'mesa';
      byChannel[channel].total += amt;
      byChannel[channel].count += 1;
    }
    // itemCount es sólo informativo (cantidad de ítems en el log).
    const itemCount = flatRows.reduce((s, r) => s + r.quantity, 0);
    // Parte de los cobros cuyo pedido HOY figura como anulado. Se informa
    // por separado; NO se descuenta, porque no hay registro de reembolso.
    let cancelledAmount = 0;
    let cancelledCount = 0;
    for (const p of payments) {
      if (cancelledOrderIds.has(String(p.order_id))) {
        cancelledAmount += Number(p.amount) || 0;
        cancelledCount += 1;
      }
    }
    const unpaidTotal = unpaidSales.reduce((s, u) => s + u.total, 0);
    return {
      byMethod: cc.byMethod,
      byChannel,
      grand,
      itemCount,
      paymentCount: cc.paymentsCount,
      cancelledAmount,
      cancelledCount,
      unpaidTotal
    };
  }, [payments, orderMeta, flatRows, cancelledOrderIds, unpaidSales]);
  const handleExportCsv = () => {
    const headers = ['Fecha y hora', 'Pedido', 'Producto', 'Cantidad', 'Precio unitario', 'Total línea', 'Método de pago', 'Canal', 'Notas'];
    const escape = (v: any) => {
      const s = String(v ?? '');
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [headers.join(',')];
    for (const r of flatRows) {
      lines.push([escape(fmtFull(r.created_at)), escape(r.order_number || r.order_id.slice(0, 8)), escape(r.item_name), escape(r.quantity), escape(r.unit_price), escape(r.line_total), escape(methodMeta(r.method).label), escape(channelMeta(r.channel, r.table_label).label), escape(r.notes || '')].join(','));
    }
    // Append totals — el TOTAL corresponde a cobros registrados
    // (SUM(payments.amount)), igual que la tarjeta principal.
    lines.push('');
    lines.push(`TOTAL COBROS REGISTRADOS,,,,,${totals.grand},,,`);
    if (totals.cancelledCount > 0) {
      lines.push(`INCLUYE COBROS DE PEDIDOS HOY ANULADOS (${totals.cancelledCount}),,,,,${totals.cancelledAmount},,,`);
    }
    // Ventas sin pago registrado: sección APARTE, con su propio total.
    // No forman parte del total de cobros.
    if (unpaidSales.length > 0) {
      lines.push('');
      lines.push('VENTAS SIN PAGO REGISTRADO (no incluidas en el total)');
      lines.push(['Fecha y hora', 'Pedido', 'Total pedido', 'Método'].join(','));
      for (const u of unpaidSales) {
        lines.push([escape(fmtFull(u.created_at)), escape(u.order_number || u.order_id.slice(0, 8)), escape(u.total), escape(methodMeta(u.method).label)].join(','));
      }
      lines.push(`TOTAL VENTAS SIN PAGO REGISTRADO,,${totals.unpaidTotal},`);
    }
    const csv = lines.join('\n');
    const blob = new Blob([`\uFEFF${csv}`], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cierre-caja-la-cabana_${startDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <Card className="border-amber-200" data-id="element-363" data-mp="334vd78vgftd7">
      <CardHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3" data-id="element-364" data-mp="3366p5vz207fe">
        <div className="flex items-center gap-3" data-id="element-365" data-mp="uxgouaw2ca1x">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center" data-id="element-366" data-mp="zwbik7vkjj0t">
            <Receipt className="w-5 h-5 text-amber-600" data-id="element-367" data-mp="kyred36hxrvp" />
          </div>
          <div data-id="element-368" data-mp="l0vi9l4eaqvz">
            <CardTitle className="text-lg" data-id="element-369" data-mp="pxc6efblh592">Cierre de Caja</CardTitle>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-370" data-mp="pzoy3k75mtzb">
              Ventas detalladas {isSingleDay ? 'del día' : 'del rango'} con
              producto, precio, método de pago y canal.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-371" data-mp="289t1jnztykd">
          <button onClick={() => load()} disabled={loading} className="h-9 w-9 rounded-lg flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors disabled:opacity-50 relative" title={rangeIsToday ? 'Refrescar (auto cada 20s)' : 'Refrescar'} data-id="element-372" data-mp="29uln3qqz7fl">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} data-id="element-373" data-mp="3apr06d0hblhm" />
            {rangeIsToday && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-white" data-id="element-374" data-mp="35rpfjnnene41" />}
          </button>
          <Button variant="secondary" size="sm" onClick={handleExportCsv} disabled={loading || flatRows.length === 0} data-id="element-375" data-mp="3jom2vf4bedg6">
            <Download className="w-4 h-4 mr-2" data-id="element-376" data-mp="3tnbmpdfru7z2" />
            CSV
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-5" data-id="element-377" data-mp="3opjlttx5fbiq">
        {/* ── Filtro de rango INTERNO (independiente del filtro global
            de MetricsPage). Mismos chips: Hoy / Ayer / Esta Semana /
            Este Mes / Último Mes / 3 Meses + Desde/Hasta. ── */}
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-3" data-id="element-378" data-mp="g9gke5wmqj2u">
          <MetricsDateFilter startDate={startDate} endDate={endDate} onChange={(s, e) => {
          setStartDate(s);
          setEndDate(e);
        }} data-id="element-379" data-mp="bb5do41msoou" />
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-380" data-mp="3vwamtr3u6pes">
          <div className="rounded-xl border border-gray-100 bg-gradient-to-br from-amber-50 to-white p-3" data-id="element-381" data-mp="3qvwkmpox29oo">
            <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider" data-id="element-382" data-mp="3lwjp7gjzbuah">
              Cobros registrados
            </p>
            <p className="text-2xl font-black text-gray-900 tabular-nums mt-1" data-id="element-383" data-mp="3h0mim1yiq2aw">
              {formatCurrency(totals.grand)}
            </p>
            <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-384" data-mp="385oowsnjqlii">
              {totals.paymentCount} cobro
              {totals.paymentCount === 1 ? '' : 's'} · {totals.itemCount} ítem
              {totals.itemCount === 1 ? '' : 's'}
            </p>
          </div>
          {(['cash', 'sinpe', 'card'] as const).map(m => {
          const meta = methodMeta(m);
          const t = totals.byMethod[m] || {
            total: 0,
            count: 0
          };
          return <div key={m} className="rounded-xl border border-gray-100 bg-white p-3" data-id="element-385" data-mp="2o3kcr4wtbouf">
                <div className="flex items-center gap-1.5" data-id="element-386" data-mp="2t4hr16epbyqn">
                  <meta.Icon className="w-3.5 h-3.5 text-gray-500" data-id="element-387" data-mp="2y2t3zq0ampd3" />
                  <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider" data-id="element-388" data-mp="2xzx5o0kj9yo4">
                    {meta.label}
                  </p>
                </div>
                <p className="text-xl font-black text-gray-900 tabular-nums mt-1" data-id="element-389" data-mp="25e3yles7fxvj">
                  {formatCurrency(t.total)}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5" data-id="element-390" data-mp="2f35dt2qxeb8g">
                  {t.count} cobro{t.count === 1 ? '' : 's'}
                </p>
              </div>;
        })}
        </div>

        {!error && !loading && totals.cancelledCount > 0 && <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-id="element-391" data-mp="1ml9wvmjle299">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" data-id="element-392" data-mp="19nliyvjiv5fp" />
            <p data-id="element-393" data-mp="19n21ffxsaj27">
              Incluye {formatCurrency(totals.cancelledAmount)} en{' '}
              {totals.cancelledCount} cobro
              {totals.cancelledCount === 1 ? '' : 's'} de pedidos que hoy
              figuran como anulados. No se descuentan: no hay un reembolso
              registrado que lo respalde.
            </p>
          </div>}

        {/* Channel breakdown */}
        <div className="flex flex-wrap gap-2" data-id="element-394" data-mp="qqal9fdjgk2d">
          {(['mesa', 'delivery', 'pickup'] as const).map(c => {
          const meta = channelMeta(c, null);
          const t = totals.byChannel[c] || {
            total: 0,
            count: 0
          };
          return <div key={c} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold ${meta.chip}`} data-id="element-395" data-mp="2gzc6tc7gatng">
                <meta.Icon className="w-3.5 h-3.5" data-id="element-396" data-mp="2jka5ytlr2ftr" />
                <span data-id="element-397" data-mp="2jno8q2ybsr1q">{meta.label}</span>
                <span className="tabular-nums" data-id="element-398" data-mp="2okodmajfvnr9">{formatCurrency(t.total)}</span>
                <span className="opacity-60" data-id="element-399" data-mp="2akwxo49d3xam">· {t.count}</span>
              </div>;
        })}
        </div>

        {/* Table */}
        {error ? <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-id="element-400" data-mp="2eobc4qhnk5ly">
            {error}
          </div> : loading ? <div className="space-y-2" data-id="element-401" data-mp="379uqhgxjxl55">
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-10 w-full rounded-lg" data-id="element-402" data-mp="2tk13l45lxay2" />)}
          </div> : flatRows.length === 0 ? <div className="text-center py-10 px-4 text-gray-500 text-sm border border-dashed border-gray-200 rounded-xl" data-id="element-403" data-mp="2xjjm5t9yh9xq">
            <p className="font-medium text-gray-700" data-id="element-404" data-mp="3l8kp5100rsvm">
              No hay ventas cerradas registradas para{' '}
              {isSingleDay ? 'esta fecha' : 'el rango'}.
            </p>
            <p className="text-xs text-gray-500 mt-2 max-w-md mx-auto leading-relaxed" data-id="element-405" data-mp="22edlbinwa6ht">
              Aparecen automáticamente las órdenes cobradas o entregadas desde
              Caja, Dashboard, Mesas y cualquier otro flujo. Si no ves datos de
              un día anterior, verifica que esas órdenes tengan estado entregado
              o método de pago registrado.
            </p>
            {rangeIsToday && <p className="text-[11px] text-emerald-700 mt-3 inline-flex items-center gap-1.5" data-id="element-406" data-mp="2l5eic15gfryq">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-407" data-mp="2g6tfrv2qcw74" />
                Actualización automática activa
              </p>}
          </div> : <div className="overflow-x-auto -mx-6" data-id="element-408" data-mp="27bwz6hgokh98">
            <table className="w-full text-sm text-left min-w-[760px]" data-id="element-409" data-mp="33ul642l3v9re">
              <thead className="text-[11px] text-gray-500 uppercase bg-gray-50" data-id="element-410" data-mp="2z3cuczwazcb2">
                <tr data-id="element-411" data-mp="2z18fxo0s2uwj">
                  <th className="px-6 py-2.5 rounded-l-lg" data-id="element-412" data-mp="lkoy9oogfo2q">Hora</th>
                  <th className="px-3 py-2.5" data-id="element-413" data-mp="lmjggv8cd234">Pedido</th>
                  <th className="px-3 py-2.5" data-id="element-414" data-mp="qj06xw30jsjd">Producto</th>
                  <th className="px-3 py-2.5 text-center" data-id="element-415" data-mp="qlmomojmbofb">Cant.</th>
                  <th className="px-3 py-2.5 text-right" data-id="element-416" data-mp="ujtugocjn5bq">Precio</th>
                  <th className="px-3 py-2.5" data-id="element-417" data-mp="zkq99wyfobvx">Método</th>
                  <th className="px-3 py-2.5 rounded-r-lg" data-id="element-418" data-mp="zidjwyqciz8u">Canal</th>
                </tr>
              </thead>
              <tbody data-id="element-419" data-mp="19invyg0na0mf">
                {flatRows.map((r, idx) => {
              const mMeta = methodMeta(r.method);
              const cMeta = channelMeta(r.channel, r.table_label);
              return <tr key={`${r.payment_id}-${idx}`} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50" data-id="element-420" data-mp="1ibheqnfavi51" data-mpi={idx}>
                      <td className="px-6 py-2.5 text-gray-700 tabular-nums whitespace-nowrap" data-id="element-421" data-mp="1s8vt8hhnxwup">
                        {fmtTime(r.created_at)}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 text-xs tabular-nums whitespace-nowrap" data-id="element-422" data-mp="3oa7e0hu5tswq">
                        #
                        {r.order_number ? r.order_number.split('-').pop() : r.order_id.slice(0, 6)}
                      </td>
                      <td className="px-3 py-2.5" data-id="element-423" data-mp="3t9jwkqe00dti">
                        <div className="font-medium text-gray-900" data-id="element-424" data-mp="aw2lblm29uua">
                          {r.item_name}
                        </div>
                        {r.notes && <div className="text-[11px] text-gray-500 italic" data-id="element-425" data-mp="kw3518nuecz0">
                            {r.notes}
                          </div>}
                      </td>
                      <td className="px-3 py-2.5 text-center text-gray-700 tabular-nums" data-id="element-426" data-mp="2mmeqp5yn4s29">
                        {r.quantity}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-gray-900 tabular-nums whitespace-nowrap" data-id="element-427" data-mp="2hpy1e8fpg7nl">
                        {formatCurrency(r.line_total)}
                      </td>
                      <td className="px-3 py-2.5" data-id="element-428" data-mp="126yoguwya4p8">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold ${mMeta.chip}`} data-id="element-429" data-mp="ngrg580nq229">
                          <mMeta.Icon className="w-3 h-3" data-id="element-430" data-mp="ef1pw5iih962" />
                          {mMeta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5" data-id="element-431" data-mp="jcbexk1znitd">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold ${cMeta.chip}`} data-id="element-432" data-mp="4i6tbs5stiuf">
                          <cMeta.Icon className="w-3 h-3" data-id="element-433" data-mp="1yuwo0c3zqw2v" />
                          {cMeta.label}
                        </span>
                      </td>
                    </tr>;
            })}
              </tbody>
              <tfoot data-id="element-434" data-mp="rk2yoofr219i">
                <tr className="border-t-2 border-gray-200 bg-amber-50/50" data-id="element-435" data-mp="rmpa4z5rh0x0">
                  <td colSpan={4} className="px-6 py-3 font-black text-gray-900" data-id="element-436" data-mp="mmkz3onmgye6">
                    TOTAL COBROS REGISTRADOS
                  </td>
                  <td className="px-3 py-3 text-right font-black text-amber-600 text-base tabular-nums" data-id="element-437" data-mp="ctvqd1jghymj">
                    {formatCurrency(totals.grand)}
                  </td>
                  <td colSpan={2} className="px-3 py-3 text-xs text-gray-500" data-id="element-438" data-mp="3tqlay1qjcbj">
                    {totals.paymentCount} cobros · {totals.itemCount} ítems
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>}

        {/* ─── Ventas sin pago registrado (respaldo histórico) ─── */}
        {!error && !loading && unpaidSales.length > 0 && <div className="rounded-xl border border-gray-200 overflow-hidden" data-id="element-439" data-mp="1oboh8aulzwnm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 px-4 py-3 bg-gray-50" data-id="element-440" data-mp="1vtmnm6hhurmm">
              <div data-id="element-441" data-mp="20s69k6w383ez">
                <p className="text-sm font-bold text-gray-900" data-id="element-442" data-mp="20ro3kijmiatv">
                  Ventas sin pago registrado
                </p>
                <p className="text-[11px] text-gray-500" data-id="element-443" data-mp="2ajand3a11i88">
                  Pedidos cerrados en el rango sin ningún cobro en todo el
                  historial. No se suman a los cobros registrados.
                </p>
              </div>
              <p className="text-lg font-black text-gray-900 tabular-nums" data-id="element-444" data-mp="1hvobl7qnb7aa">
                {formatCurrency(totals.unpaidTotal)}
                <span className="ml-1.5 text-xs font-medium text-gray-500" data-id="element-445" data-mp="1mw2lywfpytbh">
                  · {unpaidSales.length}
                </span>
              </p>
            </div>
            <div className="overflow-x-auto max-h-72" data-id="element-446" data-mp="2y8mvdztmbk8y">
              <table className="w-full text-sm text-left min-w-[480px]" data-id="element-447" data-mp="337zi8kkhorbj">
                <thead className="text-[11px] text-gray-500 uppercase bg-white sticky top-0" data-id="element-448" data-mp="33a2gfrvspqrc">
                  <tr data-id="element-449" data-mp="2y8zk1sio6674">
                    <th className="px-4 py-2" data-id="element-450" data-mp="335q1oka4h9un">Fecha</th>
                    <th className="px-3 py-2" data-id="element-451" data-mp="33687a7iod0f8">Pedido</th>
                    <th className="px-3 py-2" data-id="element-452" data-mp="384je09doaut8">Método indicado</th>
                    <th className="px-4 py-2 text-right" data-id="element-453" data-mp="387fdi2565rjs">Total pedido</th>
                  </tr>
                </thead>
                <tbody data-id="element-454" data-mp="3h60s5m7ismfa">
                  {unpaidSales.map(u => <tr key={u.order_id} className="border-t border-gray-100" data-id="element-455" data-mp="3h3oehl8soy81">
                      <td className="px-4 py-2 text-gray-700 tabular-nums whitespace-nowrap" data-id="element-456" data-mp="3qval14a6u94g">
                        {fmtDateCR(u.created_at)} {fmtTimeCR(u.created_at)}
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-xs tabular-nums" data-id="element-457" data-mp="4p5b8n7jcsuo">
                        #
                        {u.order_number ? u.order_number.split('-').pop() : u.order_id.slice(0, 6)}
                      </td>
                      <td className="px-3 py-2 text-gray-700" data-id="element-458" data-mp="1p5vbxojzqqev">
                        {methodMeta(u.method).label}
                      </td>
                      <td className="px-4 py-2 text-right font-bold text-gray-900 tabular-nums" data-id="element-459" data-mp="1k7traz6x2j1a">
                        {formatCurrency(u.total)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}

        {/* ─── Anulaciones y eliminaciones del turno ─── */}
        <div className="mt-8 pt-8 border-t border-gray-200" data-id="element-460" data-mp="235e7lhvm89aa">
          <div className="flex items-center gap-2 mb-4" data-id="element-461" data-mp="233tdtuh4kudh">
            <Ban className="w-5 h-5 text-red-500" data-id="element-462" data-mp="1y6a0cws94lml" />
            <h3 className="text-lg font-black text-gray-900" data-id="element-463" data-mp="2vmgsos5j3d64">
              Anulaciones y eliminaciones del turno
            </h3>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-464" data-mp="2quys457f3elo">
            <div className="rounded-xl border border-red-100 bg-red-50 p-3" data-id="element-465" data-mp="oyggn58p2woe">
              <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider" data-id="element-466" data-mp="p0kilhn19lm1">
                Monto Anulado
              </p>
              <p className="text-xl font-black text-red-900 tabular-nums mt-1" data-id="element-467" data-mp="f96nrn6119z8">
                {formatCurrency(voidLogs.reduce((sum, log) => sum + Number(log.amount || 0), 0))}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3" data-id="element-468" data-mp="aad7xo39p6vw">
              <p className="text-[10px] font-bold text-gray-600 uppercase tracking-wider" data-id="element-469" data-mp="3sfggjisg0112">
                Eventos
              </p>
              <p className="text-xl font-black text-gray-900 tabular-nums mt-1" data-id="element-470" data-mp="19f5vbce39fy">
                {voidLogs.length}
              </p>
            </div>
          </div>

          {voidError ? <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-id="element-471" data-mp="1vlbznayb0re6">
              {voidError}
            </div> : voidLogs.length === 0 ? <div className="text-center py-6 px-4 text-gray-500 text-sm border border-dashed border-gray-200 rounded-xl bg-gray-50/50" data-id="element-472" data-mp="20sa323yk5b58">
              <p className="font-medium" data-id="element-473" data-mp="2ek6q3tldcuur">
                No hay anulaciones registradas en este rango.
              </p>
            </div> : <div className="overflow-x-auto -mx-6 sm:mx-0" data-id="element-474" data-mp="2ogjrldn5od02">
              <table className="w-full text-sm text-left min-w-[600px]" data-id="element-475" data-mp="2oiwapu4ngtep">
                <thead className="text-[11px] text-gray-500 uppercase bg-gray-50" data-id="element-476" data-mp="2jhgm5s39k4rk">
                  <tr data-id="element-477" data-mp="2xggdj7i5uxrf">
                    <th className="px-4 py-2 rounded-l-lg" data-id="element-478" data-mp="2xgyg0lvf287s">Fecha</th>
                    <th className="px-3 py-2" data-id="element-479" data-mp="2th7ky5yux5xg">Hora</th>
                    <th className="px-3 py-2" data-id="element-480" data-mp="133qjmprslok1">Acción</th>
                    <th className="px-3 py-2" data-id="element-481" data-mp="1348o29olz931">Detalle</th>
                    <th className="px-3 py-2" data-id="element-482" data-mp="z2esgft9sv9q">Autorizado por</th>
                    <th className="px-3 py-2" data-id="element-483" data-mp="z4809yfmdljb">Motivo</th>
                    <th className="px-4 py-2 text-right rounded-r-lg" data-id="element-484" data-mp="10i90zn8735xl">Monto</th>
                  </tr>
                </thead>
                <tbody data-id="element-485" data-mp="15gu3xueu03pq">
                  {voidLogs.map(log => <tr key={log.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50" data-id="element-486" data-mp="19l1snrlahjny">
                      <td className="px-4 py-2.5 text-gray-700 tabular-nums whitespace-nowrap" data-id="element-487" data-mp="ht2quolgvmne">
                        {fmtDateCR(log.created_at)}
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 tabular-nums whitespace-nowrap" data-id="element-488" data-mp="ls1rhx060x91">
                        {fmtTimeCR(log.created_at)}
                      </td>
                      <td className="px-3 py-2.5" data-id="element-489" data-mp="vlsbaolckjie">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-bold ${log.action_type === 'cancel_order' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`} data-id="element-490" data-mp="3uar6c1a9azf2">
                          {log.action_type === 'cancel_order' ? 'Orden Completa' : 'Artículo'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5" data-id="element-491" data-mp="3dqs6ljv9e2jo">
                        <div className="font-medium text-gray-900" data-id="element-492" data-mp="3dscy10ma15db">
                          {log.action_type === 'cancel_order' ? `Orden #${log.order_id}` : log.detail}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 font-medium" data-id="element-493" data-mp="2z1dsjygqmwxy">
                        {log.authorized_name}
                      </td>
                      <td className="px-3 py-2.5 text-gray-500 text-xs italic max-w-[200px] truncate" title={log.reason} data-id="element-494" data-mp="2q0xr0ujk1zvq">
                        {log.reason}
                      </td>
                      <td className="px-4 py-2.5 text-right font-bold text-red-600 tabular-nums whitespace-nowrap" data-id="element-495" data-mp="2bftrixudzxul">
                        {formatCurrency(log.amount)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </CardContent>
    </Card>;
}